import { useCallback, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { AuthContext } from './AuthContext';
import type { AuthContextValue, AuthUser, UserInfo } from './types';
import { getAvailableRoutes, getPrimaryUserRole } from './roles';

interface StoredSession {
  user: AuthUser;
  token: string;
  availableRoutes: string[];
}

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [session, setSession] = useState<StoredSession | null>(null);

  const triggerInit = useCallback((userInfo: UserInfo): void => {
    const roles = [...userInfo.userRoles];
    const role = getPrimaryUserRole(roles);
    const user: AuthUser = {
      id: userInfo.userName,
      name: userInfo.userName,
      email: userInfo.userName,
      roles: [...roles],
      role
    };
    const availableRoutes = getAvailableRoutes(roles);

    setSession((current) => {
      if (
        current?.token === userInfo.token &&
        current?.user.name === user.name &&
        current?.user.role === user.role &&
        current?.user.roles.length === user.roles.length &&
        current.user.roles.every((value, index) => value === user.roles[index])
      ) {
        return current;
      }

      return {
        user,
        token: userInfo.token,
        availableRoutes
      };
    });
  }, []);

  const signout = useCallback((): void => {
    setSession(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      token: session?.token,
      availableRoutes: session?.availableRoutes ?? [],
      isAuthenticated: Boolean(session?.token),
      triggerInit,
      signout
    }),
    [session, triggerInit, signout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
